import { useMemo } from 'react';
import { MAX_COL_WIDTH } from 'src/App.config';
import useEntityInfo from 'src/hooks/getEntityInfo';

import { EMPTY_CELL } from '../../collectionUtils';
import type {
  RiskSmartColumnDefinition,
  TableFormConfigurationRow,
  TableRecord,
  TableRecordColumnWidths,
} from '../types';
import { useFormConfigRegistry } from './form/useFormConfigRegistry';
import { getColumnHeader } from './getColumnHeader';
import type { TableFieldsWithCustomAttributes } from './useAddCustomAttributeFieldData';

type Options<T extends TableRecord> = {
  tableFields: TableFieldsWithCustomAttributes<T>;
  columnWidths?: TableRecordColumnWidths<T>;
  labelFormConfigurations: TableFormConfigurationRow[] | null;
};

// Definition lives in `../types` (next to `TablePropsWithActions`, whose
// `columnDefinitions` it types); re-exported here as this hook is the only
// place the augmented columns are constructed.
export type { RiskSmartColumnDefinition } from '../types';

/**
 * Creates cloudscape compatible table column definitions based on RiskSmarts
 * definitions, as well as ensuring column widths are appropriately set. Why
 * does Risksmart have a different format for field definitions? this was
 * implemented in an attempt to reduce duplications, and mistakes between column
 * definitions, filter definitions and table preferences
 *
 * @param param0
 * @returns
 */
export const useCreateColumnDefinitions = <T extends TableRecord>({
  tableFields,
  columnWidths,
  labelFormConfigurations,
}: Options<T>): RiskSmartColumnDefinition<T>[] => {
  const formRegistry = useFormConfigRegistry();
  const getEntityInfo = useEntityInfo();

  return useMemo((): RiskSmartColumnDefinition<T>[] => {
    return Object.entries(tableFields).flatMap(([fieldName, fieldConfig]) => {
      const cfg = fieldConfig as Partial<{ formId: string; fieldId: string }>;
      if (cfg.formId && cfg.fieldId) {
        const formCfg = formRegistry[
          cfg.formId as keyof typeof formRegistry
        ] as Record<string, { available?: boolean } | undefined> | undefined;
        if (formCfg?.[cfg.fieldId]?.available === false) {
          return [];
        }
      }
      const {
        filterOptions: _filterOptions,
        headerTooltip,
        ...field
      } = fieldConfig;
      const headerText = getColumnHeader(
        {
          formConfigurations: labelFormConfigurations,
          formRegistry,
          getEntityInfo,
        },
        fieldConfig
      );

      return {
        ...field,
        header: headerTooltip ? (
          <span title={headerTooltip}>{headerText}</span>
        ) : (
          headerText
        ),
        headerText,
        id: fieldName,
        width: columnWidths?.[fieldName],
        maxWidth: field.maxWidth ?? MAX_COL_WIDTH,
        sortingField: !field.sortingDisabled
          ? (field.sortingField ?? fieldName)
          : undefined,
        cell:
          field.cell ??
          ((item) => <>{(item[fieldName] as string) ?? EMPTY_CELL}</>),
      };
    });
  }, [
    columnWidths,
    tableFields,
    formRegistry,
    getEntityInfo,
    labelFormConfigurations,
  ]);
};
